import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { Box, Typography } from "@mui/material";
import TotalMetrics from "./metrics/total-metrics.jsx";
import TotalTable from "./metrics/total-table.jsx";
import ChartLegend from "./chart-legend.jsx";
import {
  defaultChartLegendList,
  calculateChartDataset,
  getPercentageChange,
  defaultChartOptions,
  getSelectedPlatforms,
  formatPercentage,
} from "./utils.js";
import EmptyChartState from "./empty-chart-state.jsx";
import "../../../styles/analytics/charts.scss";

const AnalyticsChart = ({
  title,
  description,
  aggregationAnalyticsList,
  accountCheckList,
  metricFields,
  metricTable,
  defaultChartData,
  datasetOptions = {},
  showMetricTable = true,
  showChartLegend = true,
  currentPeriodRange,
  comparisonPeriodRange,
  isLoading,
  totalType = "sum",
  disableCompareData,
  stacked,
  excludeMetrics = [],
  percentMetric = false, // if true displays values in chart and totals table as percentages
}) => {
  const [selectedMetric, setSelectedMetric] = useState(metricFields[0].key);
  const [chartDatasets, setChartDatasets] = useState(
    metricFields.reduce((res, metric) => {
      res[metric.key] = [];
      return res;
    }, {})
  );
  const [totals, setTotals] = useState();
  const [providerTotals, setProviderTotals] = useState();

  const [chartLegendList, setChartLegendList] = useState();

  const isEmptyChart = !aggregationAnalyticsList.currentPeriodData.length;

  const chartRef = useRef();
  const [chartOptions, setChartOptions] = useState({ ...defaultChartOptions });

  useEffect(() => {
    chartRef?.current && chartRef.current.update();
  }, [chartRef.current, aggregationAnalyticsList]);

  useEffect(() => {
    if (defaultChartData) {
      setTotals(defaultChartData.totals);
      setProviderTotals(defaultChartData.providerTotals);
    }
  }, [defaultChartData]);

  useEffect(() => {
    if (!metricFields.find((metric) => metric.key === selectedMetric)) {
      setSelectedMetric(metricFields[0].key);
    }
  }, [metricFields]);

  useEffect(() => {
    const selectedPlatforms = getSelectedPlatforms(accountCheckList);
    const accKeys = Object.keys(accountCheckList).filter((platformName) =>
      selectedPlatforms.includes(platformName)
    );
    setChartLegendList(
      defaultChartLegendList.filter(
        (legend) => !!accKeys.find((key) => legend.key === key)
      )
    );
  }, [accountCheckList, selectedMetric]);

  useEffect(() => {
    if (defaultChartData && chartLegendList) {
      const generalTotals = { ...defaultChartData.totals };
      let tempProviderTotals = { ...defaultChartData.providerTotals };

      const tempDatasets = metricFields.reduce((res, metric) => {
        res[metric.key] = [];
        return res;
      }, {});

      if (Object.keys(accountCheckList).length) {
        metricFields.forEach((metricField) => {
          const { datasets, totals, totalsByProvider } = calculateChartDataset({
            key: metricField.key,
            totalsByProvider: { ...tempProviderTotals },
            chartLegendList,
            accountCheckList,
            aggregationAnalyticsList,
            datasetOptions,
            currentPeriodRange,
            comparisonPeriodRange,
            aggregateType: metricField.aggregateType,
            excludeMetrics,
          });
          generalTotals[metricField.key] = totals;
          tempDatasets[metricField.key] = datasets;
          tempProviderTotals = { ...tempProviderTotals, ...totalsByProvider };
        });

        setTotals(generalTotals);
        setChartDatasets(tempDatasets);
        setProviderTotals(tempProviderTotals);
      }
    }
  }, [accountCheckList, defaultChartData, chartLegendList]);

  const getCurrentDataSet = () => {
    return chartDatasets[selectedMetric].filter((dataset) => {
      // TODO just test the condition here
      if (
        chartLegendList.find(
          (legend) => legend.key === dataset.key && legend.show
        )
      ) {
        return true;
      } else false;
    });
  };

  const getTotalRows = () => {
    const selectedPlatforms = getSelectedPlatforms(accountCheckList);
    return Object.keys(providerTotals)
      .filter((platformName) => selectedPlatforms.includes(platformName))
      .map((key) => ({
        title: `${
          defaultChartLegendList.find((item) => item.key === key).label
        } \
        ${metricTable.rows[selectedMetric]}`,
        total: providerTotals[key][selectedMetric],
        percent: providerTotals[key].percentList[selectedMetric],
        key,
      }))
      .filter(() =>
        metricTable.includeFun ? metricTable.includeFun(selectedMetric) : true
      )
      .filter(
        (legend) =>
          !excludeMetrics.find(
            (item) =>
              legend.key === item.key &&
              item.metrics.find((metric) => metric === selectedMetric)
          )
      );
  };

  const getTotalHeaders = () => {
    if (totalType === "sum") {
      return [
        {
          ...metricTable.headers[selectedMetric],
          total: totals[selectedMetric].totalCount,
          currentTotalCount: totals[selectedMetric].currentTotalCount,
          comparisonTotalCount: totals[selectedMetric].comparisonTotalCount,
          percent: totals[selectedMetric].totalPercent,
        },
      ];
    } else {
      const providerKeys = Object.keys(providerTotals);
      let currentTotalCount = 0;
      providerKeys.forEach((key) => {
        currentTotalCount +=
          providerTotals[key][selectedMetric].currentTotalCount;
      });
      currentTotalCount = currentTotalCount / providerKeys.length;

      let comparisonTotalCount = 0;
      providerKeys.forEach((key) => {
        comparisonTotalCount +=
          providerTotals[key][selectedMetric].comparisonTotalCount;
      });
      comparisonTotalCount = comparisonTotalCount / providerKeys.length;

      return [
        {
          ...metricTable.headers[selectedMetric],
          total: totals[selectedMetric].totalCount,
          currentTotalCount,
          comparisonTotalCount,
          percent: -getPercentageChange(
            comparisonTotalCount,
            currentTotalCount
          ),
        },
      ];
    }
  };

  const onTriggerLegend = (key) => {
    setChartLegendList(
      chartLegendList.map((legend) => {
        if (legend.key === key) {
          return { ...legend, show: !legend.show };
        }
        return legend;
      })
    );
  };

  return (
    <Box className="chart-container">
      <Box className="info-container">
        <Typography className="chart-title">{title}</Typography>
        <Typography className="chart-description">{description}</Typography>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="https://d23ypky16g08sh.cloudfront.net/res/tail-spin.svg"
            style={{ width: "100px", marginRight: "20px" }}
          />
        </Box>
      ) : (
        <>
          {metricFields.length > 1 && totals && (
            <TotalMetrics
              metricList={metricFields.map((metric) => ({
                label: metric.label,
                tooltip: metric.tooltip,
                negativeMetric: metric.negativeMetric,
                percentage: totals[metric.key].totalPercent,
                total: totals[metric.key].totalCount,
                isSelected: selectedMetric === metric.key,
                onClick: () => setSelectedMetric(metric.key),
              }))}
              disableCompareData={disableCompareData}
            />
          )}

          {!isEmptyChart ? (
            <>
              {chartLegendList && (
                <>
                  <Box sx={{ height: "361px" }}>
                    <Line
                      ref={(ref) => (chartRef.current = ref)}
                      options={{
                        ...chartOptions,
                        scales: {
                          ...chartOptions.scales,
                          x: {
                            ...chartOptions.scales.x,
                            min: currentPeriodRange.startDate,
                            max: new Date(
                              currentPeriodRange.endDate.valueOf()
                            ).setHours(0, 0, 0),
                          },
                          y: {
                            ...chartOptions.scales.y,
                            stacked,
                            ticks: {
                              ...chartOptions.scales.y.ticks,
                              callback: function(value) { return  percentMetric ? `${Math.round(value * 100)}%` : value }
                            }
                          },
                        },
                        plugins: {
                          ...chartOptions.plugins,
                          tooltip: {
                            callbacks: {
                              label: function(context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += percentMetric ? formatPercentage(context.parsed.y): context.parsed.y;
                                }
                                return label;
                              }
                            }
                            }
                          }
                        }
                      }
                      data={{
                        datasets: getCurrentDataSet(),
                      }}
                      height={360}
                    />
                  </Box>
                  {showChartLegend && (
                    <ChartLegend
                      data={chartLegendList.filter(
                        (legend) =>
                          !excludeMetrics.find(
                            (item) =>
                              legend.key === item.key &&
                              item.metrics.find(
                                (metric) => metric === selectedMetric
                              )
                          )
                      )}
                      onTriggerLegend={onTriggerLegend}
                    />
                  )}
                </>
              )}

              {showMetricTable && totals && (
                <TotalTable
                  title={metricTable.title}
                  headers={getTotalHeaders()}
                  rows={getTotalRows()}
                  disableCompareData={disableCompareData}
                  percentMetric={percentMetric}
                />
              )}
            </>
          ) : (
            <EmptyChartState />
          )}
        </>
      )}
    </Box>
  );
};

export default AnalyticsChart;
